import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { http } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { ShieldCheck, Mail, ArrowRight, ArrowLeft, Smartphone, RefreshCw } from "lucide-react";

const fade = {
  hidden: { opacity: 0, y: 14 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: 0.05 * i, duration: 0.4, ease: [0.22, 1, 0.36, 1] } }),
};

export function TwoFactorChallenge({ pendingToken, email, emailMasked, onAuthenticated, onBack }) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    timer.current = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer.current);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return toast.error("Masukkan 6 digit kode");
    setSubmitting(true);
    try {
      const { data } = await http.post("/auth/2fa/verify", { pending_token: pendingToken, code });
      toast.success("Verifikasi berhasil");
      onAuthenticated(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kode salah");
      setCode("");
    } finally { setSubmitting(false); }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    try {
      await http.post("/auth/2fa/resend", { pending_token: pendingToken });
      toast.success("Kode baru telah dikirim ke email Anda");
      setCooldown(30);
    } catch (err) {
      const d = err.response?.data?.detail || "Gagal mengirim ulang kode";
      toast.error(d);
      const m = /(\d+)\s*detik/.exec(d);
      if (m) setCooldown(parseInt(m[1], 10));
    }
  };

  return (
    <motion.div initial="hidden" animate="show" variants={fade} className="space-y-5" data-testid="twofa-challenge">
      {onBack && (
        <button type="button" onClick={onBack}
          className="flex items-center gap-1.5 text-xs font-semibold text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="twofa-cancel">
          <ArrowLeft size={14} /> Batal
        </button>
      )}
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center justify-center h-9 w-9 rounded-xl" style={{ background: "#F6EE23" }}>
          <ShieldCheck size={18} className="text-black" />
        </span>
        <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-zinc-500">Verifikasi Email</span>
      </div>

      <div>
        <h2 className="font-outfit text-2xl font-extrabold tracking-tight text-zinc-900">Masukkan kode dari email</h2>
        <p className="text-sm text-zinc-500 mt-1.5 flex items-center gap-1.5 flex-wrap">
          <Mail size={14} className="text-zinc-400" /> Kami mengirim kode 6 digit ke
          <b className="text-zinc-700">{emailMasked || email}</b>
        </p>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="otp" className="text-[11px] font-bold tracking-wide text-zinc-800 uppercase">Kode 6 digit</Label>
          <Input id="otp" autoFocus inputMode="numeric" maxLength={6}
            value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
            placeholder="123456" data-testid="twofa-code-input"
            className="h-14 rounded-xl font-mono-num tracking-[0.5em] text-center text-2xl text-zinc-900 bg-zinc-50 border-zinc-200 focus-visible:border-zinc-900 focus-visible:ring-2 focus-visible:ring-yellow-300/60 placeholder:tracking-normal placeholder:text-base placeholder:text-zinc-400" />
        </div>
        <Button type="submit" disabled={submitting}
          className="group w-full h-14 rounded-xl text-black text-base font-extrabold tracking-wide shadow-lg shadow-yellow-400/25 hover:-translate-y-0.5 transition-transform"
          style={{ background: "#F6EE23" }} data-testid="twofa-submit">
          <span className="flex items-center justify-center gap-2">
            {submitting ? "Memproses…" : "Verifikasi & Masuk"}
            {!submitting && <ArrowRight size={18} className="transition-transform group-hover:translate-x-0.5" />}
          </span>
        </Button>
      </form>

      <button type="button" onClick={resend} disabled={cooldown > 0}
        className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-zinc-500 hover:text-zinc-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="twofa-resend">
        <RefreshCw size={14} /> {cooldown > 0 ? `Kirim ulang kode (${cooldown}s)` : "Tidak menerima kode? Kirim ulang"}
      </button>

      <div className="flex items-start gap-2 text-[11px] text-zinc-400 rounded-lg bg-zinc-50 p-3" data-testid="twofa-trust-note">
        <Smartphone size={14} className="mt-0.5 shrink-0" />
        Perangkat ini akan diingat selama 30 hari — Anda tidak perlu memasukkan kode lagi saat login di perangkat & browser yang sama.
      </div>
    </motion.div>
  );
}
